/**
 * Results Display
 * Step 4: Display final insurance summary with copy/download options
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  TextField,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import { useNavigate } from 'react-router-dom';
import { useFlashMessagesContext } from '../../contexts/FlashMessagesContext';
import type { GetReportResponseCompleted } from '../../types/insurance';
import ConditionsByBodySystem from './ConditionsByBodySystem';
import DiseaseModal from '../DiseaseModal';
import TrupanionResultsDisplay from './TrupanionResultsDisplay';

interface ResultsDisplayProps {
  results: GetReportResponseCompleted;
  onReset: () => void;
}

const INSURANCE_PROMPT_EXAMPLES = [
  'Assess this pet\'s insurability based on the medical history',
  'Which pre-existing conditions would likely affect coverage?',
  'Summarize the risk profile for underwriting',
  'Are there any breed-related predispositions to flag?',
];

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ results, onReset }) => {
  // Delegate to Trupanion display if outputFormat is trupanion
  if (results.outputFormat === 'trupanion') {
    return <TrupanionResultsDisplay results={results} onReset={onReset} />;
  }

  const { addMessage } = useFlashMessagesContext();
  const navigate = useNavigate();
  const [diseaseModalOpen, setDiseaseModalOpen] = useState(false);
  const [selectedDiseaseId, setSelectedDiseaseId] = useState<number>(0);
  const [selectedDiseaseName, setSelectedDiseaseName] = useState<string>('');
  const [chatInput, setChatInput] = useState('');

  const handleStartChat = (message: string) => {
    if (!message.trim()) return;
    const newConversationId = crypto.randomUUID();
    navigate(`/c/${newConversationId}`, {
      state: {
        insuranceReportId: results.reportId,
        initialMessage: message.trim(),
      },
    });
  };

  const handleDiseaseClick = (diseaseId: number, diseaseName: string) => {
    setSelectedDiseaseId(diseaseId);
    setSelectedDiseaseName(diseaseName);
    setDiseaseModalOpen(true);
  };

  const linkedCount = [...results.preExistingConditions, ...results.thingsToNote].filter(
    (c) => c.diseaseId
  ).length;

  const predisposingCount = results.preExistingConditions.filter(
    (c) => c.relatedConditions && c.relatedConditions.length > 0
  ).length;

  const formatForClipboard = (section: 'preExisting' | 'thingsToNote' | 'all'): string => {
    const lines: string[] = [];

    lines.push('='.repeat(60));
    lines.push('INSURANCE MEDICAL RECORD SUMMARY');
    lines.push('='.repeat(60));
    lines.push('');

    if (section === 'preExisting' || section === 'all') {
      lines.push('PRE-EXISTING CONDITIONS');
      lines.push('-'.repeat(60));
      if (results.preExistingConditions.length === 0) {
        lines.push('None');
      } else {
        results.preExistingConditions.forEach((condition, i) => {
          lines.push(`${i + 1}. ${condition.condition} (${condition.bodySystem})`);
          lines.push(`   Date: ${condition.dateOfVisit}`);
          lines.push(`   Hospital: ${condition.hospital}`);
          lines.push(`   Notes: ${condition.veterinarianNotes}`);
          lines.push('');
        });
      }
      lines.push('');
    }

    if (section === 'thingsToNote' || section === 'all') {
      lines.push('THINGS TO NOTE');
      lines.push('-'.repeat(60));
      if (results.thingsToNote.length === 0) {
        lines.push('None');
      } else {
        results.thingsToNote.forEach((finding, i) => {
          lines.push(`${i + 1}. ${finding.finding} (${finding.bodySystem})`);
          lines.push(`   Date: ${finding.dateOfVisit}`);
          lines.push(`   Hospital: ${finding.hospital}`);
          lines.push(`   Notes: ${finding.veterinarianNotes}`);
          lines.push('');
        });
      }
      lines.push('');
    }

    if (section === 'all') {
      lines.push('HOSPITALS VISITED');
      lines.push('-'.repeat(60));
      results.hospitalsVisited.forEach((hospital) => {
        lines.push(`- ${hospital.name}`);
        lines.push(`  ${hospital.address}`);
      });
    }

    return lines.join('\n');
  };

  const handleCopyToClipboard = async (section: 'preExisting' | 'thingsToNote' | 'all') => {
    try {
      const text = formatForClipboard(section);
      await navigator.clipboard.writeText(text);
      addMessage({
        id: crypto.randomUUID(),
        type: 'success',
        title: 'Copied to Clipboard',
        message: 'Summary has been copied to clipboard',
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      addMessage({
        id: crypto.randomUUID(),
        type: 'error',
        title: 'Copy Failed',
        message: 'Failed to copy to clipboard',
      });
    }
  };

  const handleDownloadJSON = () => {
    try {
      const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `insurance-summary-${results.reportId}.json`;
      link.click();
      URL.revokeObjectURL(url);

      addMessage({
        id: crypto.randomUUID(),
        type: 'success',
        title: 'Downloaded',
        message: 'Summary has been downloaded as JSON',
      });
    } catch (error) {
      console.error('Failed to download:', error);
      addMessage({
        id: crypto.randomUUID(),
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to download JSON file',
      });
    }
  };

  return (
    <Box>
      {/* Action buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => handleCopyToClipboard('all')}
        >
          Copy Full Summary
        </Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadJSON}>
          Download JSON
        </Button>
        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={onReset}>
          New Report
        </Button>
      </Stack>

      {/* Summary stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <Chip
          label={`${results.preExistingConditions.length} Pre-Existing Conditions`}
          color="error"
          variant="outlined"
        />
        <Chip
          label={`${results.thingsToNote.length} Things to Note`}
          color="warning"
          variant="outlined"
        />
        <Chip
          label={`${results.hospitalsVisited.length} Hospitals Visited`}
          color="info"
          variant="outlined"
        />
        {linkedCount > 0 && (
          <Chip
            label={`${linkedCount} Linked to GekkoVet DB`}
            color="primary"
            variant="outlined"
          />
        )}
        {predisposingCount > 0 && (
          <Chip
            label={`${predisposingCount} with Predisposing Conditions`}
            color="warning"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Ask about this report */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <ChatIcon color="primary" />
          <Typography variant="h6">Ask about this report</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {INSURANCE_PROMPT_EXAMPLES.map((prompt) => (
            <Chip
              key={prompt}
              label={prompt}
              onClick={() => handleStartChat(prompt)}
              clickable
              variant="outlined"
              color="primary"
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask a question about this insurance report..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleStartChat(chatInput);
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={() => handleStartChat(chatInput)}
            disabled={!chatInput.trim()}
          >
            <SendIcon />
          </IconButton>
        </Stack>
      </Paper>

      {/* Pre-Existing Conditions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Pre-Existing Conditions</Typography>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => handleCopyToClipboard('preExisting')}
          >
            Copy
          </Button>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {results.preExistingConditions.length === 0 ? (
          <Alert severity="success">No pre-existing conditions found</Alert>
        ) : (
          <ConditionsByBodySystem conditions={results.preExistingConditions} type="preExisting" onDiseaseClick={handleDiseaseClick} />
        )}
      </Paper>

      {/* Things to Note */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Things to Note</Typography>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => handleCopyToClipboard('thingsToNote')}
          >
            Copy
          </Button>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {results.thingsToNote.length === 0 ? (
          <Alert severity="info">No findings to note</Alert>
        ) : (
          <ConditionsByBodySystem conditions={results.thingsToNote} type="thingsToNote" onDiseaseClick={handleDiseaseClick} />
        )}
      </Paper>

      {/* Hospitals Visited */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Hospitals Visited
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          {results.hospitalsVisited.map((hospital, index) => (
            <ListItem key={index}>
              <ListItemText primary={hospital.name} secondary={hospital.address} />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Processed Files */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Processed Files
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          {results.processedFiles.map((file, index) => (
            <ListItem key={index}>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    {file.success ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                    <Typography>{file.filename}</Typography>
                  </Stack>
                }
                secondary={
                  file.success
                    ? `${file.findingsExtracted} findings extracted${file.dateRange ? ` (${file.dateRange.start} - ${file.dateRange.end})` : ''}`
                    : file.error || 'Processing failed'
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Metadata */}
      <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary">
          Report ID: {results.reportId} | Created: {new Date(results.metadata.createdAt).toLocaleString()} |
          Processing Time: {(results.metadata.processingTimeMs / 1000).toFixed(1)}s
        </Typography>
      </Paper>

      {/* Disease Detail Modal */}
      <DiseaseModal
        open={diseaseModalOpen}
        onClose={() => setDiseaseModalOpen(false)}
        diseaseId={selectedDiseaseId}
        diseaseName={selectedDiseaseName}
      />
    </Box>
  );
};

export default ResultsDisplay;
